import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchProperty } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chatbot from "@/components/Chatbot";
import { Bed, Bath, Maximize, MapPin, ArrowLeft, Check } from "lucide-react";

const fmtAED = (n) => `AED ${(n / 1_000_000).toFixed(1)}M`;
const fmtUSD = (n) => `USD ${(n / 1_000_000).toFixed(1)}M`;

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    fetchProperty(slug).then(setP).catch(() => setErr(true));
  }, [slug]);

  if (err) {
    return (
      <main className="bg-[#f5f0e8] min-h-screen flex items-center justify-center text-[#1c1c1c]">
        <div className="text-center">
          <div className="underline-label mb-4">Not Found</div>
          <Link to="/" className="font-display text-3xl text-[#c5a059]">Return to AZEFAC</Link>
        </div>
      </main>
    );
  }
  if (!p) return <main className="bg-[#f5f0e8] min-h-screen" />;

  return (
    <main data-testid="property-detail" className="bg-[#f5f0e8] text-[#1c1c1c] grain">
      <Navbar solid />
      <section className="pt-32 pb-16 max-w-[1400px] mx-auto px-6 lg:px-12">
        <Link to="/" className="inline-flex items-center gap-2 underline-label text-[#3d2b1f] hover:text-[#c5a059] mb-10">
          <ArrowLeft size={14} /> Back to Collection
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-8">
            <div className="underline-label mb-4">{p.type} · {p.status}</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
              {p.title}
            </h1>
            <div className="mt-6 flex items-center gap-2 text-[#3d2b1f]">
              <MapPin size={15} className="text-[#c5a059]" /> {p.area}
            </div>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <div className="underline-label mb-2">Guide Price</div>
            <div className="font-display text-4xl text-[#1c1c1c]">{fmtAED(p.price_aed)}</div>
            <div className="text-sm text-[#3d2b1f] mt-1">{fmtUSD(p.price_usd)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
          <div className="img-zoom aspect-[4/3] md:row-span-2 md:aspect-[4/5]">
            <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
          </div>
          {p.images.slice(1).map((src, i) => (
            <div key={i} className="img-zoom aspect-[4/3]">
              <img src={src} alt={`${p.title} ${i}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-[#1c1c1c]/10 py-10 mb-16">
          <div>
            <div className="underline-label mb-2">Bedrooms</div>
            <div className="font-display text-3xl"><Bed size={16} className="inline text-[#c5a059] mr-2" />{p.bedrooms}</div>
          </div>
          <div>
            <div className="underline-label mb-2">Bathrooms</div>
            <div className="font-display text-3xl"><Bath size={16} className="inline text-[#c5a059] mr-2" />{p.bathrooms}</div>
          </div>
          <div>
            <div className="underline-label mb-2">Size</div>
            <div className="font-display text-3xl"><Maximize size={16} className="inline text-[#c5a059] mr-2" />{p.size_sqft.toLocaleString()}</div>
            <div className="text-xs text-[#3d2b1f] mt-1">sqft</div>
          </div>
          <div>
            <div className="underline-label mb-2">Type</div>
            <div className="font-display text-3xl">{p.type}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="underline-label mb-4">The Residence</div>
            <p className="text-[#3d2b1f] text-lg leading-relaxed">{p.description}</p>
          </div>
          <div className="lg:col-span-5">
            <div className="underline-label mb-4">Signature Features</div>
            <ul className="space-y-3">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-3 py-3 border-b border-[#1c1c1c]/10">
                  <Check size={14} className="text-[#c5a059]" />
                  <span className="text-[#1c1c1c]">{f}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/#contact"
              data-testid="property-cta-private-viewing"
              className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-[#c5a059] text-[#1c1c1c] uppercase text-[11px] tracking-[0.32em] hover:bg-[#1c1c1c] hover:text-[#f5f0e8] transition-colors"
            >
              Request Private Viewing
            </Link>
          </div>
        </div>
      </section>
      <Footer />
      <Chatbot />
    </main>
  );
}
